import { Controller } from "@hotwired/stimulus"
import videojs from "video.js"

// Import Video.js CSS
import "video.js/dist/video-js.css"

export default class extends Controller {
  static targets = ["video"]
  static values = {
    sources: Array,
    subtitles: Object,
    audioTracks: Object,
    defaultLanguage: String
  }

  // LocalStorage keys for preferences
  static SUBTITLE_PREFERENCE_KEY = 'video-player-preferred-subtitle'
  static AUDIO_PREFERENCE_KEY = 'video-player-preferred-audio'

  connect() {
    this.isInitialized = false
    this.tracksAdded = false
    this.audioTrackChangeListenerAdded = false
    this.subtitleTrackChangeListenerAdded = false
    
    // Simple audio sync setup
    this.currentAudioElement = null
    this.loadedAudioElements = new Map()
    this.isOriginalAudio = true
    
    this.initializePlayer()
  }

  disconnect() {
    console.log('Disconnecting video player controller...')
    
    this.stopCurrentAudio()
    this.cleanupAudioElements()
    
    if (this.player) {
      this.player.dispose()
      this.player = null
    }
    
    console.log('Video player controller disconnected')
  }

  initializePlayer() {
    if (!this.hasVideoTarget || this.sourcesValue.length === 0) {
      return
    }

    // Debug logging
    console.log('Video player initialization:', {
      sources: this.sourcesValue.length,
      subtitles: this.subtitlesValue ? Object.keys(this.subtitlesValue).length : 0,
      audioTracks: this.audioTracksValue ? Object.keys(this.audioTracksValue).length : 0,
      defaultLanguage: this.defaultLanguageValue
    })
    
    // Initialize Video.js player with simple configuration
    this.player = videojs(this.videoTarget, {
      controls: true,
      responsive: true,
      fluid: true,
      preload: 'metadata',
      sources: this.sourcesValue,
      playbackRates: [0.5, 1, 1.25, 1.5, 2],
      preferFullWindow: true,
      textTrackSettings: true,
      muted: false,
      crossorigin: 'anonymous',
      html5: {
        nativeTextTracks: false,
        nativeAudioTracks: false,
        nativeVideoTracks: false
      },
      fullscreen: {
        options: {
          navigationUI: 'hide'
        }
      },
      controlBar: {
        volumePanel: false,
        muteToggle: false,
        audioTrackButton: true
      }
    })

    // Enhanced player ready callback
    this.player.ready(() => {
      console.log('Video.js player is ready')
      
      // Add tracks once when ready
      if (!this.tracksAdded) {
        // Wait a bit more for the player to be fully initialized
        setTimeout(() => {
          this.addTracksWhenReady()
          this.tracksAdded = true
        }, 100)
      }
      
      this.isInitialized = true
    })

    // Error handling
    this.player.on('error', (event) => {
      console.error('Video.js player error:', event)
      console.error('Error details:', this.player.error())
    })
  }

  addTracksWhenReady() {
    console.log('Adding tracks to player...')
    
    // Add subtitles first
    this.addSubtitles()
    
    // Add audio tracks
    this.addAudioTracks()
    
    // Preload audio elements
    this.preloadAudioElements()
    
    // Apply saved preferences after tracks are added and audio elements are preloaded
    setTimeout(() => {
      this.setupTrackChangeListeners()
      // Wait a bit more for audio elements to be ready before applying preferences
      setTimeout(() => {
        this.applySavedPreferences()
      }, 200)
    }, 500)
    
    // Log the final state after a short delay to ensure all tracks are processed
    setTimeout(() => {
      console.log('Final tracks state:', {
        textTracks: this.player.textTracks().length,
        audioTracks: this.player.audioTracks().length,
        textTrackList: Array.from(this.player.textTracks()).map(track => ({
          kind: track.kind,
          language: track.language,
          label: track.label,
          mode: track.mode
        })),
        audioTrackList: Array.from(this.player.audioTracks()).map(track => ({
          kind: track.kind,
          language: track.language,
          label: track.label,
          enabled: track.enabled
        }))
      })
    }, 1000)
  }

  addSubtitles() {
    if (!this.subtitlesValue || Object.keys(this.subtitlesValue).length === 0) {
      console.log('No subtitles to add')
      return
    }

    console.log('Adding subtitles:', this.subtitlesValue)

    // Clear any existing remote text tracks to prevent duplicates
    const existingTracks = this.player.remoteTextTracks()
    for (let i = existingTracks.length - 1; i >= 0; i--) {
      this.player.removeRemoteTextTrack(existingTracks[i])
    }

    // Add each subtitle track to the player
    Object.entries(this.subtitlesValue).forEach(([language, subtitleUrl], index) => {
      console.log(`Adding subtitle track: ${language} -> ${subtitleUrl}`)
      
      const trackElement = this.player.addRemoteTextTrack({
        kind: 'subtitles',
        src: subtitleUrl,
        srclang: language,
        label: this.getLanguageLabel(language),
        default: false // Never set subtitles as default - user must manually enable
      }, false) // false = don't add to DOM manually
      
      console.log('Added subtitle track:', trackElement)
    })
  }

  addAudioTracks() {
    // Clear any existing audio tracks to prevent duplicates
    const existingAudioTracks = this.player.audioTracks()
    for (let i = existingAudioTracks.length - 1; i >= 0; i--) {
      existingAudioTracks.removeTrack(existingAudioTracks[i])
    }

    // Always add the original video audio as the first option
    const originalAudioTrack = new videojs.AudioTrack({
      id: 'audio-original',
      kind: 'main',
      label: 'English (Original)',
      language: 'en',
      enabled: true // This is the default - original video audio
    })
    
    this.player.audioTracks().addTrack(originalAudioTrack)
    console.log('Added original video audio track')

    if (!this.audioTracksValue || Object.keys(this.audioTracksValue).length === 0) {
      console.log('No additional audio tracks to add')
      return
    }

    console.log('Adding uploaded audio tracks:', this.audioTracksValue)

    // Add each uploaded audio track to the player
    Object.entries(this.audioTracksValue).forEach(([language, audioUrl], index) => {
      console.log(`Adding uploaded audio track: ${language} -> ${audioUrl}`)
      
      const audioTrack = new videojs.AudioTrack({
        id: `audio-${language}`,
        kind: 'translation', // All uploaded tracks are translations
        label: this.getLanguageLabel(language),
        language: language,
        enabled: false // Not enabled by default - original video audio is default
      })

      // Store the URL for potential switching (this is custom property)
      audioTrack.src = audioUrl
      audioTrack.audioUrl = audioUrl // Also store as audioUrl for compatibility
      
      this.player.audioTracks().addTrack(audioTrack)
      console.log(`Added uploaded audio track: ${audioTrack.id} (${language}) -> ${audioUrl}`)
    })
    
    // Log final audio tracks state
    console.log('Final audio tracks added:', Array.from(this.player.audioTracks()).map(track => ({
      id: track.id,
      language: track.language,
      label: track.label,
      enabled: track.enabled
    })))
  }

  preloadAudioElements() {
    if (!this.audioTracksValue || Object.keys(this.audioTracksValue).length === 0) {
      console.log('No audio tracks to preload')
      return
    }

    console.log('Starting to preload audio elements for:', Object.keys(this.audioTracksValue))

    // Create and preload audio elements for each track
    Object.entries(this.audioTracksValue).forEach(([language, audioUrl]) => {
      console.log(`Preloading audio for language: ${language}, URL: ${audioUrl}`)
      
      if (!this.loadedAudioElements.has(language)) {
        const audioElement = new Audio()
        audioElement.preload = 'metadata'
        audioElement.crossOrigin = 'anonymous'
        audioElement.src = audioUrl
        
        // Add event listeners to track loading progress
        audioElement.addEventListener('loadedmetadata', () => {
          console.log(`Audio metadata loaded for ${language}`)
        })
        
        audioElement.addEventListener('canplay', () => {
          console.log(`Audio element ready to play for ${language}`)
        })
        
        audioElement.addEventListener('error', (error) => {
          console.error(`Audio loading error for ${language}:`, error)
          console.error(`Failed URL: ${audioUrl}`)
        })
        
        // Store in our map
        this.loadedAudioElements.set(language, audioElement)
        console.log(`Stored audio element for language: ${language}`)
      } else {
        console.log(`Audio element already exists for language: ${language}`)
      }
    })
    
    // Log the final state of loaded audio elements
    setTimeout(() => {
      console.log('=== Preloaded Audio Elements Summary ===')
      console.log('Loaded audio elements count:', this.loadedAudioElements.size)
      console.log('Available languages:', Array.from(this.loadedAudioElements.keys()))
      this.loadedAudioElements.forEach((audioElement, language) => {
        console.log(`- ${language}: ready=${audioElement.readyState >= 1}, src=${audioElement.src}`)
      })
      console.log('=== End Audio Elements Summary ===')
    }, 2000)
  }

  setupTrackChangeListeners() {
    console.log('Setting up track change listeners...')
    
    // Listen for audio track changes (only add listener once)
    if (!this.audioTrackChangeListenerAdded) {
      console.log('Adding audio track change listener')
      const audioTracks = this.player.audioTracks()
      console.log('Audio tracks object:', audioTracks)
      console.log('Audio tracks length:', audioTracks.length)
      
      this.audioTrackChangeHandler = () => {
        console.log('Audio track change event fired!')
        this.handleAudioTrackChange()
      }
      
      audioTracks.addEventListener('change', this.audioTrackChangeHandler)
      this.audioTrackChangeListenerAdded = true
      console.log('Audio track change listener added successfully')
      

    } else {
      console.log('Audio track change listener already added')
    }

    // Listen for subtitle track changes (only add listener once)
    if (!this.subtitleTrackChangeListenerAdded) {
      console.log('Adding subtitle track change listener')
      const textTracks = this.player.textTracks()
      
      this.subtitleTrackChangeHandler = () => {
        console.log('Subtitle track change event fired!')
        this.handleSubtitleTrackChange()
      }
      
      textTracks.addEventListener('change', this.subtitleTrackChangeHandler)
      this.subtitleTrackChangeListenerAdded = true
      console.log('Subtitle track change listener added successfully')
    } else {
      console.log('Subtitle track change listener already added')
    }
  }

  applySavedPreferences() {
    console.log('Applying saved preferences...')
    
    // Apply saved subtitle preference
    this.applySavedSubtitlePreference()
    
    // Apply saved audio preference
    this.applySavedAudioPreference()
  }

  applySavedSubtitlePreference() {
    const savedSubtitleLang = localStorage.getItem(this.constructor.SUBTITLE_PREFERENCE_KEY)
    if (!savedSubtitleLang) {
      console.log('No saved subtitle preference found')
      return
    }

    console.log(`Attempting to restore subtitle preference: ${savedSubtitleLang}`)

    // Find the matching subtitle track
    const textTracks = this.player.textTracks()
    for (let i = 0; i < textTracks.length; i++) {
      const track = textTracks[i]
      if (track.language === savedSubtitleLang && track.kind === 'subtitles') {
        console.log(`Restoring subtitle track: ${track.language} (${track.label})`)
        track.mode = 'showing'
        this.showTrackInfo(`Subtitles: ${track.label}`, 'subtitle')
        break
      }
    }
  }

  applySavedAudioPreference() {
    const savedAudioLang = localStorage.getItem(this.constructor.AUDIO_PREFERENCE_KEY)
    if (!savedAudioLang) {
      console.log('No saved audio preference found - using original audio as default')
      // Ensure original audio track is enabled by default
      const audioTracks = this.player.audioTracks()
      for (let i = 0; i < audioTracks.length; i++) {
        const track = audioTracks[i]
        if (track.id === 'audio-original') {
          track.enabled = true
          this.switchToOriginalAudio()
          this.showTrackInfo('English (Original)', 'audio')
          localStorage.setItem(this.constructor.AUDIO_PREFERENCE_KEY, 'original')

          break
        }
      }
      return
    }

    console.log(`Attempting to restore audio preference: ${savedAudioLang}`)

    // Find the matching audio track
    const audioTracks = this.player.audioTracks()
    console.log(`Available audio tracks (${audioTracks.length}):`, 
      Array.from(audioTracks).map(track => ({
        id: track.id,
        language: track.language,
        label: track.label,
        enabled: track.enabled
      }))
    )

    let trackFound = false
    for (let i = 0; i < audioTracks.length; i++) {
      const track = audioTracks[i]
      
      // Check for original track
      if (savedAudioLang === 'original' && track.id === 'audio-original') {
        console.log('Found and restoring original audio track')
        // Disable all other tracks first
        for (let j = 0; j < audioTracks.length; j++) {
          audioTracks[j].enabled = false
        }
        track.enabled = true
        // Actually switch to original audio
        this.switchToOriginalAudio()
        this.showTrackInfo('English (Original)', 'audio')
        trackFound = true
        break
      }
      // Check for uploaded tracks
      else if (track.language === savedAudioLang && track.id.startsWith('audio-')) {
        console.log(`Found and restoring audio track: ${track.language} (${track.label})`)
        // Disable all other tracks first
        for (let j = 0; j < audioTracks.length; j++) {
          audioTracks[j].enabled = false
        }
        track.enabled = true
        // Actually switch to the separate audio
        this.playSeparateAudio(track.language, track.label)
        trackFound = true
        break
      }
    }

    if (!trackFound) {
      console.log(`No matching audio track found for saved preference: ${savedAudioLang}`)
    }
  }

  handleSubtitleTrackChange() {
    const textTracks = this.player.textTracks()
    let activeSubtitleLang = null
    
    // Find the currently active subtitle track
    for (let i = 0; i < textTracks.length; i++) {
      const track = textTracks[i]
      if (track.kind === 'subtitles' && track.mode === 'showing') {
        activeSubtitleLang = track.language
        console.log(`Subtitle track changed to: ${track.language} (${track.label})`)
        this.showTrackInfo(`Subtitles: ${track.label}`, 'subtitle')
        break
      }
    }

    // Save preference to localStorage
    if (activeSubtitleLang) {
      localStorage.setItem(this.constructor.SUBTITLE_PREFERENCE_KEY, activeSubtitleLang)
      console.log(`Saved subtitle preference: ${activeSubtitleLang}`)
    } else {
      // If no subtitles are showing, clear the preference
      localStorage.removeItem(this.constructor.SUBTITLE_PREFERENCE_KEY)
      console.log('Cleared subtitle preference (subtitles disabled)')
    }
  }

  handleAudioTrackChange() {
    console.log('Audio track change detected')
    const audioTracks = this.player.audioTracks()
    
    for (let i = 0; i < audioTracks.length; i++) {
      const track = audioTracks[i]
      
      if (track.enabled) {
        if (track.id === 'audio-original') {
          console.log('Switching to original video audio')
          this.player.volume(1.0)
          this.stopCurrentAudio()
          this.isOriginalAudio = true
          this.showTrackInfo('English (Original)', 'audio')
          localStorage.setItem(this.constructor.AUDIO_PREFERENCE_KEY, 'original')
        } else {
          console.log(`Switching to separate audio: ${track.language}`)
          this.player.volume(0)
          this.playSeparateAudio(track.language, track.label)
          localStorage.setItem(this.constructor.AUDIO_PREFERENCE_KEY, track.language)
        }
        break
      }
    }
  }

  playSeparateAudio(language, label) {
    // Stop any current audio
    this.stopCurrentAudio()
    
    // Get the audio element
    const audioElement = this.loadedAudioElements.get(language)
    if (!audioElement) {
      console.error(`No audio element for language: ${language}`)
      console.error('Available:', Array.from(this.loadedAudioElements.keys()))
      // Fall back to original
      this.player.volume(1.0)
      return
    }

    // Set as current and sync to video time
    this.currentAudioElement = audioElement
    this.isOriginalAudio = false
    this.currentAudioElement.currentTime = this.player.currentTime()
    this.currentAudioElement.volume = 1.0

    // Play if video is playing
    if (!this.player.paused()) {
      this.currentAudioElement.play().catch(error => {
        console.error('Audio play failed:', error)
      })
    }

    // Set up basic sync
    this.setupBasicSync()
    this.showTrackInfo(label || this.getLanguageLabel(language), 'audio')
  }

  setupBasicSync() {
    // Remove old listeners
    this.removeAudioVideoSync()

    this.playListener = () => {
      if (this.currentAudioElement && this.currentAudioElement.paused) {
        this.currentAudioElement.currentTime = this.player.currentTime()
        this.currentAudioElement.play()
      }
    }

    this.pauseListener = () => {
      if (this.currentAudioElement) {
        this.currentAudioElement.pause()
      }
    }

    this.seekedListener = () => {
      if (this.currentAudioElement) {
        this.currentAudioElement.currentTime = this.player.currentTime()
      }
    }

    this.player.on('play', this.playListener)
    this.player.on('pause', this.pauseListener)
    this.player.on('seeked', this.seekedListener)
  }

  stopCurrentAudio() {
    if (this.currentAudioElement) {
      this.currentAudioElement.pause()
      this.currentAudioElement.currentTime = 0
    }
    this.removeAudioVideoSync()
  }

  // Old switchToSeparateAudio method removed - using playSeparateAudio instead

  switchToOriginalAudio() {
    // Stop separate audio and sync
    this.stopCurrentAudio()
    
    // Restore original video volume
    this.player.volume(1.0)
    
    // Remove audio/video sync listeners
    this.removeAudioVideoSync()
    
    this.isOriginalAudio = true
    this.currentAudioElement = null
    
    console.log('Switched back to original video audio')
  }

  // Old setupAudioVideoSync method removed - using setupBasicSync instead

  removeAudioVideoSync() {
    if (this.pauseListener) {
      this.player.off('pause', this.pauseListener)
      this.pauseListener = null
    }
    if (this.playListener) {
      this.player.off('play', this.playListener)
      this.playListener = null
    }
    if (this.seekedListener) {
      this.player.off('seeked', this.seekedListener)
      this.seekedListener = null
    }
    if (this.timeupdateListener) {
      this.player.off('timeupdate', this.timeupdateListener)
      this.timeupdateListener = null
    }
  }

  cleanupAudioElements() {
    // Clean up all preloaded audio elements
    this.loadedAudioElements.forEach(audioElement => {
      audioElement.pause()
      audioElement.src = ''
      audioElement.load()
    })
    this.loadedAudioElements.clear()
    this.currentAudioElement = null
  }

  showTrackInfo(message, type = 'info') {
    // Create a temporary notification
    const notification = document.createElement('div')
    notification.className = `video-track-notification video-track-${type}`
    
    const backgroundColor = type === 'audio' ? 'rgba(59, 130, 246, 0.9)' : 'rgba(16, 185, 129, 0.9)'
    
    notification.style.cssText = `
      position: absolute;
      top: 10px;
      right: 10px;
      background: ${backgroundColor};
      color: white;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 14px;
      z-index: 1000;
    `
    notification.textContent = message
    
    // Add to player container
    const playerContainer = this.player.el()
    playerContainer.style.position = 'relative'
    playerContainer.appendChild(notification)
    
    // Remove after 3 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification)
      }
    }, 3000)
  }

  showAudioError(message) {
    // Create an error notification
    const notification = document.createElement('div')
    notification.className = 'video-audio-error'
    notification.style.cssText = `
      position: absolute;
      top: 10px;
      right: 10px;
      background: rgba(220, 38, 38, 0.9);
      color: white;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 14px;
      z-index: 1000;
    `
    notification.textContent = message
    
    // Add to player container
    const playerContainer = this.player.el()
    playerContainer.style.position = 'relative'
    playerContainer.appendChild(notification)
    
    // Remove after 5 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification)
      }
    }, 5000)
  }

  getLanguageLabel(languageCode) {
    const languageLabels = {
      'en': 'English',
      'fr': 'Français',
      'es': 'Español', 
      'de': 'Deutsch',
      'it': 'Italiano',
      'pt': 'Português',
      'nl': 'Nederlands',
      'ru': 'Русский',
      'ja': '日本語',
      'ko': '한국어',
      'zh': '中文',
      'ar': 'العربية',
      'hi': 'हिंदी'
    }
    
    return languageLabels[languageCode] || languageCode.toUpperCase()
  }

  // Debug method to manually test audio track switching
  debugAudioTracks() {
    console.log('=== AUDIO TRACKS DEBUG ===')
    const audioTracks = this.player.audioTracks()
    console.log(`Total audio tracks: ${audioTracks.length}`)
    
    for (let i = 0; i < audioTracks.length; i++) {
      const track = audioTracks[i]
      console.log(`Track ${i}:`, {
        id: track.id,
        language: track.language,
        label: track.label,
        enabled: track.enabled,
        kind: track.kind,
        src: track.src,
        audioUrl: track.audioUrl
      })
    }
    
    console.log('Loaded audio elements:', Array.from(this.loadedAudioElements.keys()))
    console.log('Current audio element:', this.currentAudioElement ? 'exists' : 'null')
    console.log('Is original audio:', this.isOriginalAudio)
    console.log('Event listener added:', this.audioTrackChangeListenerAdded)
    console.log('Video playing:', !this.player.paused())
    console.log('Video volume:', this.player.volume())
    
    if (this.currentAudioElement) {
      console.log('Current audio element state:', {
        paused: this.currentAudioElement.paused,
        currentTime: this.currentAudioElement.currentTime,
        duration: this.currentAudioElement.duration,
        readyState: this.currentAudioElement.readyState,
        volume: this.currentAudioElement.volume
      })
    }
    
    console.log('=== END DEBUG ===')
    
    // Return a helper object for manual testing
    return {
      switchToLanguage: (language) => {
        console.log(`Manually switching to language: ${language}`)
        const track = Array.from(audioTracks).find(t => t.language === language)
        if (track) {
          // Disable all tracks first
          for (let i = 0; i < audioTracks.length; i++) {
            audioTracks[i].enabled = false
          }
          // Enable the requested track
          track.enabled = true
          console.log(`Enabled track for ${language}, triggering change event`)
        } else {
          console.error(`No track found for language: ${language}`)
        }
      },
      switchToOriginal: () => {
        console.log('Manually switching to original audio')
        const originalTrack = Array.from(audioTracks).find(t => t.id === 'audio-original')
        if (originalTrack) {
          // Disable all tracks first
          for (let i = 0; i < audioTracks.length; i++) {
            audioTracks[i].enabled = false
          }
          // Enable original track
          originalTrack.enabled = true
          console.log('Enabled original track, triggering change event')
        } else {
          console.error('No original track found')
        }
      },
      listTracks: () => {
        return Array.from(audioTracks).map(track => ({
          id: track.id,
          language: track.language,
          label: track.label,
          enabled: track.enabled
        }))
      }
    }
  }
} 